import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { API, SEO_CONFIG } from "@/utils/helpers";

const AdminPage = () => {
  const [loading, setLoading] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [zeroResults, setZeroResults] = useState(null);
  const [loadingZeroResults, setLoadingZeroResults] = useState(false);
  const pollInterval = useRef(null);

  // Fetch cache status
  const fetchCacheStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/cache-status`);
      setCacheStatus(response.data);
    } catch (err) {
      console.error('Error fetching cache status:', err);
    }
  };

  // Fetch zero-result queries
  const fetchZeroResults = async () => {
    setLoadingZeroResults(true);
    try {
      const response = await axios.get(`${API}/admin/zero-results`);
      setZeroResults(response.data);
    } catch (err) {
      console.error('Error fetching zero results:', err);
    } finally {
      setLoadingZeroResults(false);
    }
  };

  // Poll for status updates when a refresh is running
  const pollStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/refresh-status`);
      setStatus(response.data);
      
      // Stop polling if refresh is complete
      if (!response.data.running && pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
        setLoading(false);
        
        if (response.data.error) {
          setError(`❌ Erreur: ${response.data.error}`);
        } else if (response.data.last_completed) {
          setMessage(`✅ ${response.data.message}`);
          // Refresh cache status and zero results after completion
          fetchCacheStatus();
          fetchZeroResults();
        }
      }
    } catch (err) {
      console.error('Error polling status:', err);
    }
  };

  // Check initial status on mount
  useEffect(() => {
    pollStatus();
    fetchCacheStatus();
    fetchZeroResults();
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post(`${API}/admin/refresh-data`);
      
      if (response.data.success) {
        setMessage(`🔄 ${response.data.message}`);
        
        // Start polling for status
        pollInterval.current = setInterval(pollStatus, 2000);
      } else {
        setError(`⚠️ ${response.data.message}`);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error starting refresh:', err);
      setError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post(`${API}/admin/clear-cache`);
      
      if (response.data.success) {
        setMessage(`🗑️ ${response.data.message}`);
        fetchCacheStatus();
      } else {
        setError(`⚠️ ${response.data.message}`);
      }
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError(`❌ Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setClearingCache(false);
    }
  };

  const progressPercent = status?.total > 0 
    ? Math.round((status.progress / status.total) * 100) 
    : 0;

  const totalCacheEntries = cacheStatus 
    ? Object.values(cacheStatus).reduce((sum, cache) => sum + cache.entries, 0)
    : 0;

  // Group zero results by brand
  const groupedZeroResults = zeroResults?.queries?.reduce((acc, item) => {
    if (!acc[item.brand]) {
      acc[item.brand] = [];
    }
    acc[item.brand].push(item);
    return acc;
  }, {}) || {};

  return (
    <div className="admin-page">
      <Helmet>
        <title>{`Administration | ${SEO_CONFIG.siteName}`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="admin-container">
        <h1 className="admin-title">Administration</h1>
        
        {/* Data Refresh Card */}
        <div className="admin-card">
          <h2 className="admin-card-title">Rafraîchir les données</h2>
          <p className="admin-card-description">
            Récupère les marques et requêtes depuis Google Spreadsheet, 
            exécute les recherches Kelkoo et met à jour la base de données.
            <br />
            <strong>Note:</strong> Les caches sont automatiquement vidés après le rafraîchissement.
          </p>
          
          <Button 
            onClick={handleRefresh}
            disabled={loading || status?.running}
            className="admin-refresh-button"
            data-testid="refresh-button"
          >
            {loading || status?.running ? 'Rafraîchissement en cours...' : 'Rafraîchir les données'}
          </Button>
          
          {/* Progress bar */}
          {(loading || status?.running) && status?.total > 0 && (
            <div className="admin-progress" data-testid="progress-container">
              <div className="admin-progress-bar">
                <div 
                  className="admin-progress-fill" 
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="admin-progress-text">
                {status.progress} / {status.total} ({progressPercent}%)
              </div>
              <div className="admin-progress-message">
                {status.message}
              </div>
            </div>
          )}
          
          {message && (
            <div className="admin-message success" data-testid="success-message">{message}</div>
          )}
          
          {error && (
            <div className="admin-message error" data-testid="error-message">{error}</div>
          )}
          
          {status?.last_completed && !loading && !status?.running && (
            <div className="admin-last-refresh">
              Dernière mise à jour: {new Date(status.last_completed).toLocaleString('fr-FR')}
            </div>
          )}
        </div>

        {/* Cache Status Card */}
        <div className="admin-card">
          <h2 className="admin-card-title">État des caches</h2>
          <p className="admin-card-description">
            Les données sont mises en cache pour améliorer les performances. 
            Vous pouvez vider les caches manuellement pour forcer le rechargement des données.
          </p>
          
          {cacheStatus && (
            <div className="cache-status-grid">
              <div className="cache-status-item">
                <span className="cache-label">Cache recherche</span>
                <span className="cache-value">{cacheStatus.search_cache?.entries || 0} entrées</span>
                <span className="cache-ttl">TTL: {cacheStatus.search_cache?.ttl_hours || 0}h</span>
              </div>
              <div className="cache-status-item">
                <span className="cache-label">Cache accueil</span>
                <span className="cache-value">{cacheStatus.homepage_cache?.entries || 0} entrées</span>
                <span className="cache-ttl">TTL: {cacheStatus.homepage_cache?.ttl_hours || 0}h</span>
              </div>
              <div className="cache-status-item">
                <span className="cache-label">Cache marques</span>
                <span className="cache-value">{cacheStatus.brand_data_cache?.entries || 0} entrées</span>
                <span className="cache-ttl">TTL: {cacheStatus.brand_data_cache?.ttl_hours || 0}h</span>
              </div>
              <div className="cache-status-item">
                <span className="cache-label">Cache offres</span>
                <span className="cache-value">{cacheStatus.brand_offers_cache?.entries || 0} entrées</span>
                <span className="cache-ttl">TTL: {cacheStatus.brand_offers_cache?.ttl_hours || 0}h</span>
              </div>
              <div className="cache-status-item">
                <span className="cache-label">Cache métadonnées</span>
                <span className="cache-value">{cacheStatus.brand_metadata_cache?.entries || 0} entrées</span>
                <span className="cache-ttl">TTL: {cacheStatus.brand_metadata_cache?.ttl_hours || 0}h</span>
              </div>
              <div className="cache-status-item total">
                <span className="cache-label">Total</span>
                <span className="cache-value">{totalCacheEntries} entrées</span>
              </div>
            </div>
          )}
          
          <Button 
            onClick={handleClearCache}
            disabled={clearingCache || loading || status?.running}
            className="admin-clear-cache-button"
            variant="outline"
            data-testid="clear-cache-button"
          >
            {clearingCache ? 'Vidage en cours...' : '🗑️ Vider tous les caches'}
          </Button>
        </div>

        {/* Zero Results Card */}
        <div className="admin-card">
          <h2 className="admin-card-title">
            Requêtes sans résultats 
            {zeroResults?.count > 0 && (
              <span className="zero-results-badge">{zeroResults.count}</span>
            )}
          </h2>
          <p className="admin-card-description">
            Ces requêtes n'ont retourné aucun résultat sur Kelkoo. 
            Vérifiez les fautes de frappe ou modifiez les mots-clés dans le spreadsheet.
          </p>
          
          {loadingZeroResults ? (
            <div className="admin-loading">Chargement...</div>
          ) : zeroResults?.count === 0 ? (
            <div className="admin-message success">
              ✅ Toutes les requêtes retournent des résultats !
            </div>
          ) : (
            <div className="zero-results-container" data-testid="zero-results">
              {Object.entries(groupedZeroResults).map(([brand, queries]) => (
                <div key={brand} className="zero-results-brand">
                  <h3 className="zero-results-brand-name">{brand}</h3>
                  <div className="zero-results-list">
                    {queries.map((item, idx) => (
                      <div key={idx} className="zero-results-item">
                        <span className="zero-results-query">{item.query}</span>
                        <span className="zero-results-full-query">→ {item.full_query}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <Button 
            onClick={fetchZeroResults}
            disabled={loadingZeroResults}
            className="admin-refresh-zero-button"
            variant="outline"
            data-testid="refresh-zero-button"
          >
            🔄 Actualiser la liste
          </Button>
        </div>
        
        <a href="/" className="admin-back-link">
          ← Retour à l'accueil
        </a>
      </div>
    </div>
  );
};

export default AdminPage;
